import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { to: "/work", label: "Work" },
  { to: "/services", label: "Services" },
  { to: "/impact", label: "Impact" },
  { to: "/process", label: "Process" },
  { to: "/about", label: "About" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > window.innerHeight * 0.4);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#F7F1E1]/80 border-b border-[#E3D8C1]"
      data-testid="main-nav">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="nav-logo" aria-label="seedesign">
          <img src="/seedesign-wordmark.png" alt="seedesign" className="h-7 lg:h-8 w-auto select-none" draggable="false" />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase()}-link`}
              className={`link-underline text-sm uppercase tracking-[0.15em] transition-colors hover:text-[#66371B] ${
                loc.pathname.startsWith(l.to) ? "text-[#66371B]" : "text-[#3F3F2C]"
              }`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <AnimatePresence>
            {scrolled && (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }} className="hidden sm:block">
                <Link to="/contact" data-testid="nav-cta-sticky"
                  className="rounded-full bg-[#66371B] px-5 py-2 text-sm text-[#F7F1E1] hover:bg-[#3F3F2C] transition-colors">
                  Let's create
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
          <Link to="/contact" data-testid="nav-contact-link"
            className="hidden md:block text-sm uppercase tracking-[0.15em] text-[#3F3F2C] hover:text-[#66371B] link-underline">
            Contact
          </Link>
          <button className="md:hidden text-[#3F3F2C]" onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} className="md:hidden overflow-hidden bg-[#F7F1E1] border-t border-[#E3D8C1]">
            <div className="px-6 py-4 flex flex-col gap-4">
              {[...LINKS, { to: "/contact", label: "Contact" }].map((l) => (
                <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  className="font-serif text-2xl text-[#3F3F2C]">{l.label}</Link>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
